import React, { useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import fuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import LineChart from "../../../../Components/Charts/LineChart";
import StackedBarChart from "../../../../Components/Charts/StackedBarChart";
import GenericTable from "../../../../Components/Tables/GenericTable";
import totalVendorsIcon from "../../../../Assets/Icons/storefront.svg";
import activeVendorIcon from "../../../../Assets/Icons/storefront_active.svg";
import paymentIcon from "../../../../Assets/Icons/payments_spend.svg";
import { useNavigate } from "react-router-dom";
import { useDateFilter } from "../../../../Contexts/DateFilterContext";
import { getAdminReports } from "../../../../API/Admin/Reports";

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const FuelVendors = () => {
  const navigate = useNavigate();
  const { from, to } = useDateFilter();
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getAdminReports("fuel-vendors", startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          setReportData(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load fuel vendors report:", err);
      }
    };

    fetchReport();
  }, [from, to]);

  const stats = reportData?.stats || {};
  const topVendorsBySpend = reportData?.top_vendors_by_spend;
  const fuelSpendTrend = reportData?.fuel_spend_trend;

  const handleView = (record) => {
    navigate("/vendor-detail", {
      state: { id: record.key, from: "reports-fuel-vendors" },
    });
  };

  const vendorData = (reportData?.vendor_performance || []).map((vendor) => ({
    key: String(vendor.fuel_vendor_id),
    vendor: vendor.fuel_vendor_name || "-",
    type: Array.isArray(vendor.fuel_types_offered)
      ? vendor.fuel_types_offered
      : [],
    volume: vendor.total_volume ? `${vendor.total_volume} L` : "-",
    cost: formatCurrency(vendor.total_amount),
    status: capitalize(vendor.status) || "-",
  }));

  const uniqueTypes = [...new Set(vendorData.flatMap((item) => item.type))];

  const typeFilters = uniqueTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const statusFilters = [
    ...new Set(vendorData.map((item) => item.status)),
  ].map((status) => ({ text: status, value: status }));

  const vendorColumns = [
    {
      title: "Vendors",
      dataIndex: "vendor",
      key: "vendor",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>{text}</span>
        </div>
      ),
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (types) => (
        <div
          style={{
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {types.length > 0
            ? types.map((t) => (
                <span key={t} className="type-badge">
                  {t}
                </span>
              ))
            : "-"}
        </div>
      ),

      filters: typeFilters,

      onFilter: (value, record) => record.type.includes(value),
    },
    {
      title: "Total Fuel Volume",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat(a.volume) || 0) - (parseFloat(b.volume) || 0),
    },
    {
      title: "Total Fuel Cost",
      dataIndex: "cost",
      key: "cost",
      sorter: (a, b) =>
        (parseFloat(a.cost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.cost.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => handleView(record)}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                color: "#4F46E5",
              }}
            >
              <EyeOutlined style={{ fontSize: 18 }} />
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="fuel-vendor">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Vendors"
            value={stats.total_vendors?.value ?? stats.total_vendors ?? 0}
            subtitle="vs. last month"
            icon={totalVendorsIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Active Vendors"
            value={stats.active_vendors?.value ?? stats.active_vendors ?? 0}
            subtitle="vs. last month"
            icon={activeVendorIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Fuel Volume"
            value={`${stats.total_fuel_volume?.value ?? 0} L`}
            {...(stats.total_fuel_volume?.percentage != null && {
              percentage: `${stats.total_fuel_volume.percentage}`,
            })}
            isIncrease={stats.total_fuel_volume?.is_increase}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(stats.total_spend?.value)}
            {...(stats.total_spend?.percentage != null && {
              percentage: `${stats.total_spend.percentage}`,
            })}
            isIncrease={stats.total_spend?.is_increase}
            subtitle="vs. last month"
            icon={paymentIcon}
          />
        </div>
      </div>
      <div className="row">
        <div className="graph">
          <StackedBarChart
            graphData={{
              labels: topVendorsBySpend?.labels || [],
              Volume: (topVendorsBySpend?.total_volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Amount: (topVendorsBySpend?.total_amount || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#4F46E5", "#16A34A"]}
            graphTitle="Top Vendors by Fuel Spend"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="vendor-fuel"
          />
        </div>
        <div className="graph">
          <LineChart
            graphData={{
              labels: fuelSpendTrend?.labels || [],
              Spend: (fuelSpendTrend?.spend || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Volume: (fuelSpendTrend?.volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#F59E0B", "#4F46E5"]}
            graphTitle="Fuel Spend"
            selectTenByDefault={false}
            units={["$", "L"]}
            classNameCSS="fuel-spend"
          />
        </div>
      </div>
      <div className="vendor-table">
        <GenericTable
          title="Vendor Performance"
          dataSource={vendorData}
          columns={vendorColumns}
        />
      </div>
    </div>
  );
};

export default FuelVendors;
